/** Maps a revealed or shared contact payload (loose server JSON) onto the engine's ContactInput. */
import type { ContactInput } from "@hub/shared";

type Loose = Record<string, unknown>;

function str(v: unknown): string | null {
  return typeof v === "string" && v.trim() ? v.trim() : null;
}

function list(v: unknown): string[] {
  if (Array.isArray(v)) return v.map((x) => (typeof x === "string" ? x : typeof x === "object" && x && "value" in x ? String((x as Loose).value) : "")).filter(Boolean);
  if (typeof v === "string" && v.trim()) return [v.trim()];
  return [];
}

export function payloadToContactInput(payload: Loose, fallback: { name?: string | null; email?: string | null; phone?: string | null } = {}): ContactInput {
  const name = str(payload.displayName) ?? str(payload.name) ?? fallback.name ?? null;
  const parts = name ? name.split(/\s+/) : [];
  const emails = [...list(payload.emails), ...(str(payload.email) ? [str(payload.email)!] : []), ...(fallback.email ? [fallback.email] : [])];
  const phones = [...list(payload.phones), ...(str(payload.phone) ? [str(payload.phone)!] : []), ...(fallback.phone ? [fallback.phone] : [])];
  return {
    displayName: name,
    firstName: str(payload.firstName) ?? (parts.length > 1 ? parts[0] : parts[0] ?? null),
    lastName: str(payload.lastName) ?? (parts.length > 1 ? parts.slice(1).join(" ") : null),
    headline: str(payload.headline),
    company: str(payload.company),
    title: str(payload.title),
    bio: str(payload.bio),
    location: str(payload.location),
    websites: list(payload.websites),
    emails: Array.from(new Set(emails.map((e) => e.toLowerCase()))).map((value) => ({ value })),
    phones: Array.from(new Set(phones)).map((value) => ({ value })),
    avatarUrl: str(payload.avatarUrl),
    source: "hub",
  };
}

/** The field that names the Hub user who shared a person; the person page shows it as "Shared by <name>". */
export const SHARED_BY_FIELD = "shared_by";

/**
 * A person accepted from a share: the payload the server copied, marked as coming from Hub and from the sharer. The
 * payload never carries the sharer's notes or interaction history, so nothing here can either.
 */
export function sharedContactInput(payload: Loose, sharedBy: { userId: string; name: string }): ContactInput {
  const input = payloadToContactInput(payload);
  return { ...input, sharedByUserId: sharedBy.userId, fields: { ...(input.fields ?? {}), [SHARED_BY_FIELD]: sharedBy.name } };
}
